import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';
import Sidebar from './Sidebar';
import './MarketingAdminPanel.css';

const MarketingAdminPanel = () => {
  const [clients, setClients] = useState([]);
  const [shops, setShops] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [activeTab, setActiveTab] = useState('clients');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [newStaff, setNewStaff] = useState({ name: '', email: '', role: '' });
  const [newClient, setNewClient] = useState({ name: '', contact: '', address: '' });
  const [newShop, setNewShop] = useState({ name: '', address: '', gstNumber: '' });
  const [message, setMessage] = useState('');
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showShopModal, setShowShopModal] = useState(false);

  const handlePrint = (type) => {
    const printWindow = window.open('', '_blank');
    const tableData = type === 'clients' ? clients : type === 'shops' ? shops : staffs;
    const fields = type === 'clients' ? ['name', 'contact', 'address'] : 
                   type === 'shops' ? ['name', 'address', 'gstNumber'] : 
                   ['name', 'email', 'role'];
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${type.charAt(0).toUpperCase() + type.slice(1)} Report</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .company-info {
              margin-bottom: 20px;
            }
            .report-title {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .report-date {
              font-size: 14px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #333;
              padding: 12px;
              text-align: left;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
              <h1>WYENFOS BILLS</h1>
              <p>Professional Billing System</p>
            </div>
            <div class="report-title">${type.charAt(0).toUpperCase() + type.slice(1)} Report</div>
            <div class="report-date">Generated on: ${new Date().toLocaleString()}</div>
          </div>
          
          <table>
            <thead>
              <tr>
                ${fields.map(field => `<th>${field.charAt(0).toUpperCase() + field.slice(1)}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${tableData.length > 0 ? tableData.map(item => `
                <tr>
                  ${fields.map(field => `<td>${item[field] || 'N/A'}</td>`).join('')}
                </tr>
              `).join('') : `
                <tr>
                  <td colspan="${fields.length}" style="text-align: center;">No data available</td>
                </tr>
              `}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Total ${type}: ${tableData.length}</p>
            <p>Report generated by Marketing Admin Panel</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    name: userProfile?.name || "",
    email: userProfile?.email || "",
    role: userProfile?.role || "Marketing Admin",
    profilePhoto: userProfile?.profilePhoto || "",
  });

  // Update userData when userProfile changes
  useEffect(() => {
    if (userProfile) {
      setUserData({
        name: userProfile.name || "",
        email: userProfile.email || "",
        role: userProfile.role || "Marketing Admin",
        profilePhoto: userProfile.profilePhoto || "",
      });
    }
  }, [userProfile]);

  const handleUpdateProfile = (updatedUser) => {
    setUserData(updatedUser);
  };

  const handleLogout = () => {
    // This will be handled by the AuthContext
    navigate('/login');
  };

  useEffect(() => {
    // Wait for authentication to complete before checking
    if (authLoading) {
      return;
    }
    
    if (!currentUser) {
      // Don't redirect immediately, wait for auth to load
      return;
    }

    // Wait for userProfile to load (it might be null initially on refresh)
    if (currentUser && !userProfile) {
      // User is authenticated but profile not loaded yet, wait
      return;
    }

    const cachedRole = localStorage.getItem('userRole');
    const effectiveRole = userProfile?.role || cachedRole;
    
    if (effectiveRole && !['admin', 'super_admin', 'superadmin', 'marketing_admin', 'marketingadmin'].includes(effectiveRole)) {
      navigate('/dashboard');
      return;
    }

    if (clients.length === 0 && shops.length === 0 && staffs.length === 0) {
      const fetchData = async () => {
        try {
          setLoading(true);
          const idToken = await currentUser.getIdToken(true);
          
          const fetchDataFromAPI = async (url, setter) => {
            try {
              const res = await fetch(url, {
                headers: { Authorization: `Bearer ${idToken}` },
              });
              
              if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || `Server error: ${res.status}`);
              }
              
              const data = await res.json();
              const dataKey = Object.keys(data)[0];
              if (dataKey && data[dataKey]) {
                const items = data[dataKey].map(item => ({ ...item, isEditing: false }));
                setter(items);
              } else {
                setter([]);
              }
            } catch (err) {
              setter([]);
            }
          };

          await Promise.all([
            fetchDataFromAPI(`${API_CONFIG.BASE_URL}/api/marketing/client-details`, setClients),
            fetchDataFromAPI(`${API_CONFIG.BASE_URL}/api/marketing/shop-details`, setShops),
            fetchDataFromAPI(`${API_CONFIG.BASE_URL}/api/marketing/staff-details`, setStaffs)
          ]);
        } catch (err) {
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [currentUser, authLoading, userProfile, clients.length, shops.length, staffs.length, navigate]);

  const handleEdit = (type, id) => {
    const list = type === "client" ? clients : type === "shop" ? shops : staffs;
    const updated = list.map(item => (item._id === id || item.id === id) ? { ...item, isEditing: true } : item);
    if (type === "client") setClients(updated);
    else if (type === "shop") setShops(updated);
    else setStaffs(updated);
  };

  const handleSave = (type, id) => {
    const list = type === "client" ? clients : type === "shop" ? shops : staffs;
    const updated = list.map(item => (item._id === id || item.id === id) ? { ...item, isEditing: false } : item);
    if (type === "client") setClients(updated);
    else if (type === "shop") setShops(updated);
    else setStaffs(updated);
    toast.success(`Saved ${type} with ID: ${id}`);
  };

  const handleDelete = async (type, id) => {
    if (!id) {
      toast.error(`Error: No ID found for ${type}`);
      return;
    }
    if (window.confirm(`Are you sure you want to delete this ${type}?`)) {
      if (type === 'staff') {
        try {
          const idToken = await currentUser.getIdToken(true);
          const res = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/staff-details/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || 'Failed to delete staff');
          }
          setStaffs(staffs.filter(item => (item._id !== id && item.id !== id)));
          toast.success(`Deleted ${type} with ID: ${id}`);
        } catch (err) {
          toast.error(`Error: ${err.message}`);
        }
      } else {
        try {
          const idToken = await currentUser.getIdToken(true);
          const endpoint = type === "client" ? "client-details" : "shop-details";
          const res = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/${endpoint}/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${idToken}` },
          });
          
          if (!res.ok) {
            const data = await res.json();
            throw new Error(data.error || `Failed to delete ${type}`);
          }
          
          const list = type === "client" ? clients : shops;
          const updated = list.filter(item => (item._id !== id && item.id !== id));
          if (type === "client") setClients(updated);
          else setShops(updated);
          toast.success(`Deleted ${type} with ID: ${id}`);
        } catch (err) {
          console.error(`Error deleting ${type}:`, err);
          toast.error(`Error: ${err.message}`);
        }
      }
    }
  };

  const handleChange = (e, id, field, type) => {
    const list = type === "client" ? clients : type === "shop" ? shops : staffs;
    const updated = list.map(item => (item._id === id || item.id === id) ? { ...item, [field]: e.target.value } : item);
    if (type === "client") setClients(updated);
    else if (type === "shop") setShops(updated);
    else setStaffs(updated);
  };

  const handleStaffInputChange = (e) => {
    setNewStaff({ ...newStaff, [e.target.name]: e.target.value });
  };

  const handleClientInputChange = (e) => {
    setNewClient({ ...newClient, [e.target.name]: e.target.value });
  };

  const handleShopInputChange = (e) => {
    setNewShop({ ...newShop, [e.target.name]: e.target.value });
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.role) {
      toast.error("All fields are required to add a staff member.");
      return;
    }
    try {
      if (!currentUser) {
        toast.error("No authentication found. Please log in again.");
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/staff-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(newStaff),
      });
      const data = await res.json();
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Unable to add staff member');
      }
      setStaffs([...staffs, { ...data.staff, isEditing: false }]);
      setNewStaff({ name: '', email: '', role: '' });
      toast.success("Staff added successfully!");
    } catch (err) {
      console.error("Error adding staff:", err);
      toast.error(err.message);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.contact || !newClient.address) {
      toast.error("All fields are required to add a client.");
      return;
    }
    try {
      if (!currentUser) {
        toast.error("No authentication found. Please log in again.");
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/client-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(newClient),
      });
      const data = await res.json();
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Unable to add client');
      }
      setClients([...clients, { ...data.client, isEditing: false }]);
      setNewClient({ name: '', contact: '', address: '' });
      toast.success("Client added successfully!");
    } catch (err) {
      console.error("Error adding client:", err);
      toast.error(err.message);
    }
  };

  const handleAddShop = async () => {
    if (!newShop.name || !newShop.address || !newShop.gstNumber) {
      toast.error("All fields are required to add a shop.");
      return;
    }
    try {
      if (!currentUser) {
        toast.error("No authentication found. Please log in again.");
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true);
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/shop-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(newShop),
      });
      const data = await res.json();
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Unable to add shop');
      }
      setShops([...shops, { ...data.shop, isEditing: false }]);
      setNewShop({ name: '', address: '', gstNumber: '' });
      toast.success("Shop added successfully!");
    } catch (err) {
      console.error("Error adding shop:", err);
      toast.error(err.message);
    }
  };

  const toggleStaffSelect = (id) => {
    setSelectedStaffIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const sendMessageToSelected = async () => {
    if (!message) {
      toast.error("Message cannot be empty");
      return;
    }
    if (selectedStaffIds.length === 0) {
      toast.error("Please select at least one staff member");
      return;
    }
    try {
      const idToken = await currentUser.getIdToken(true);
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ staffIds: selectedStaffIds, message }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Unable to send message');
      }
      // Message sent successfully
      toast.success(`Message sent to ${selectedStaffIds.length} staff member(s)`);
      setMessage('');
      setSelectedStaffIds([]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const renderTable = (type, data, fields) => (
    <div className="map-section">
      <div className="map-section-header">
        <h3>{type.charAt(0).toUpperCase() + type.slice(1)} Details</h3>
        <button 
          className="print-btn" 
          onClick={() => handlePrint(type)}
          title={`Print ${type} report`}
        >
          🖨️ Print {type.charAt(0).toUpperCase() + type.slice(1)}
        </button>
      </div>
      <table className="map-data-table">
        <thead>
          <tr>
            {fields.map(f => <th key={f}>{f}</th>)}
            {type === 'staff' && <th>Select</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? data.map((item) => (
            <tr key={item._id || item.id || Math.random()}>
              {fields.map((field, index) => (
                <td key={`${item._id || item.id || 'item'}-${field}-${index}`}>
                  {item.isEditing ? (
                    <input value={item[field]} onChange={(e) => handleChange(e, item._id || item.id, field, type)} />
                  ) : item[field]}
                </td>
              ))}
              {type === 'staff' && (
                <td key={`${item._id || item.id || 'item'}-select`}>
                  <input
                    type="checkbox"
                    checked={selectedStaffIds.includes(item._id || item.id)}
                    onChange={() => toggleStaffSelect(item._id || item.id)}
                  />
                </td>
              )}
              <td key={`${item._id || item.id || 'item'}-actions`}>
                <button className="action-btn edit" onClick={() => handleEdit(type, item._id || item.id)}>Edit</button>
                <button className="action-btn save" onClick={() => handleSave(type, item._id || item.id)}>Save</button>
                <button className="action-btn delete" onClick={() => handleDelete(type, item._id || item.id)}>Delete</button>
              </td>
            </tr>
          )) : <tr><td colSpan={fields.length + (type === 'staff' ? 2 : 1)}>No data available.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <div className="map-container">
        <div className="map-main-panel">
          <div className="map-loading-message">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover />
      <div className="map-animated-bg"></div>
      <div className="map-container">
        <Sidebar
          user={userData}
          setActiveTab={setActiveTab}
          handleLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onUpdateProfile={handleUpdateProfile}
        />
        <div className="map-main-panel">
          <h2>Marketing Admin Panel</h2>
          {loading && <div className="map-loading-message">Loading data...</div>}
          {activeTab === 'clients' && (
            <>
              {renderTable('client', clients, ['name', 'contact', 'address'])}
              <div className="map-section add-form">
                <h4>Add New Client</h4>
                <button className="action-btn add" onClick={() => setShowClientModal(true)}>Add New Client</button>
              </div>
            </>
          )}
          {activeTab === 'shops' && (
            <>
              {renderTable('shop', shops, ['name', 'address', 'gstNumber'])}
              <div className="map-section add-form">
                <h4>Add New Shop</h4>
                <button className="action-btn add" onClick={() => setShowShopModal(true)}>Add New Shop</button>
              </div>
            </>
          )}
          {activeTab === 'groups' && (
            <>
              {renderTable('staff', staffs, ['name', 'email', 'role'])}
              <div className="map-section add-form">
                <h4>Add New Staff</h4>
                <input name="name" placeholder="Name" value={newStaff.name} onChange={handleStaffInputChange} />
                <input name="email" placeholder="Email" value={newStaff.email} onChange={handleStaffInputChange} />
                <input name="role" placeholder="Role" value={newStaff.role} onChange={handleStaffInputChange} />
                <button className="action-btn add" onClick={handleAddStaff}>Add Staff</button>
              </div>
            </>
          )}
          {activeTab === 'messages' && (
            <div className="map-section message-section">
              <h3>Send Message to Selected Staff</h3>
              
              {/* Staff Selection Section */}
              <div className="staff-selection-container">
                <h4>Select Staff Members to Message:</h4>
                <div className="staff-list">
                  {staffs.length > 0 ? staffs.map((staff) => (
                    <div key={staff._id} className="staff-item">
                      <input
                        type="checkbox"
                        id={`staff-${staff._id}`}
                        checked={selectedStaffIds.includes(staff._id)}
                        onChange={() => toggleStaffSelect(staff._id)}
                      />
                      <label htmlFor={`staff-${staff._id}`}>
                        <strong>{staff.name}</strong> - {staff.email} ({staff.role})
                      </label>
                    </div>
                  )) : (
                    <p>No staff members available. Please add staff members first.</p>
                  )}
                </div>
                
                {selectedStaffIds.length > 0 && (
                  <div className="selected-staff-summary">
                    <strong>Selected Staff ({selectedStaffIds.length}):</strong>
                    <ul>
                      {selectedStaffIds.map(id => {
                        const staff = staffs.find(s => s._id === id);
                        return staff ? <li key={id}>{staff.name} ({staff.role})</li> : null;
                      })}
                    </ul>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <div className="message-input-container">
                <h4>Message:</h4>
                <textarea
                  placeholder="Type your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                ></textarea>
              </div>

              {/* Action Buttons */}
              <div className="message-actions">
                <button 
                  className="action-btn save" 
                  onClick={sendMessageToSelected}
                  disabled={selectedStaffIds.length === 0 || !message.trim()}
                >
                  Send Message
                </button>
                <button 
                  className="action-btn delete" 
                  onClick={() => {
                    setSelectedStaffIds([]);
                    setMessage('');
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Client Modal */}
      {showClientModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Client</h3>
              <button className="modal-close" onClick={() => setShowClientModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <input name="name" placeholder="Client Name" value={newClient.name} onChange={handleClientInputChange} />
              <input name="contact" placeholder="Contact Number" value={newClient.contact} onChange={handleClientInputChange} />
              <input name="address" placeholder="Address" value={newClient.address} onChange={handleClientInputChange} />
            </div>
            <div className="modal-footer">
              <button className="action-btn add" onClick={() => {
                handleAddClient();
                setShowClientModal(false);
              }}>Add Client</button>
              <button className="action-btn delete" onClick={() => setShowClientModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Modal */}
      {showShopModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Shop</h3>
              <button className="modal-close" onClick={() => setShowShopModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <input name="name" placeholder="Shop Name" value={newShop.name} onChange={handleShopInputChange} />
              <input name="address" placeholder="Address" value={newShop.address} onChange={handleShopInputChange} />
              <input name="gstNumber" placeholder="GST Number" value={newShop.gstNumber} onChange={handleShopInputChange} />
            </div>
            <div className="modal-footer">
              <button className="action-btn add" onClick={() => {
                handleAddShop();
                setShowShopModal(false);
              }}>Add Shop</button>
              <button className="action-btn delete" onClick={() => setShowShopModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketingAdminPanel;